/**
 * API Usage Banner Component
 * 
 * Team 플랜 사용자에게 API 사용량 경고를 글로벌 배너로 표시
 * 80% 이상: 주황색 경고
 * 100% 이상: 빨간색 초과 알림
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../auth';
import { useLanguage } from '../i18n';

export default function ApiUsageBanner() {
  const { quota } = useAuth();
  const { lang } = useLanguage();
  const [isDismissed, setIsDismissed] = useState(false);
  
  // 페이지 새로고침 시 다시 표시
  useEffect(() => {
    setIsDismissed(false);
  }, [quota?.api_usage?.total_daily_usage]);
  
  // Team 플랜이 아니거나 API 사용량 정보가 없으면 표시하지 않음
  if (!quota?.api_usage || quota.plan !== 'team') {
    return null;
  }
  
  const { total_daily_usage, total_daily_limit } = quota.api_usage;
  const usagePercent = total_daily_limit > 0 ? (total_daily_usage / total_daily_limit) * 100 : 0;
  
  // 80% 미만이면 표시하지 않음
  if (usagePercent < 80) {
    return null;
  }
  
  // 사용자가 닫았으면 표시하지 않음
  if (isDismissed) {
    return null;
  }
  
  const isExceeded = usagePercent >= 100;
  
  return (
    <div 
      className={`fixed top-0 left-0 right-0 z-[100] ${
        isExceeded 
          ? 'bg-status-danger/95' 
          : 'bg-status-warning/95'
      } backdrop-blur-sm shadow-lg`}
    >
      <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {/* 아이콘 */}
          <div className={`w-8 h-8 rounded-full ${
            isExceeded ? 'bg-white/20' : 'bg-black/10'
          } flex items-center justify-center flex-shrink-0`}>
            <svg 
              className={`w-4 h-4 ${isExceeded ? 'text-white' : 'text-black/70'}`} 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
              />
            </svg>
          </div>
          
          {/* 메시지 */}
          <div className={`text-sm ${isExceeded ? 'text-white' : 'text-black/80'}`}>
            <span className="font-medium">
              {isExceeded 
                ? (lang === 'ko' ? '🚫 API 일일 한도 초과' : '🚫 Daily API Limit Exceeded')
                : (lang === 'ko' ? '⚠️ API 일일 한도 임박' : '⚠️ API Daily Limit Warning')}
            </span>
            <span className="ml-2">
              {lang === 'ko' 
                ? `${total_daily_usage.toLocaleString()} / ${total_daily_limit.toLocaleString()}회 사용 (${Math.round(usagePercent)}%)`
                : `${total_daily_usage.toLocaleString()} / ${total_daily_limit.toLocaleString()} requests (${Math.round(usagePercent)}%)`}
            </span>
            <span className="ml-2 opacity-80">
              {lang === 'ko' ? '• 00:00 KST에 리셋됩니다' : '• Resets at 00:00 KST'}
            </span>
          </div>
        </div>
        
        {/* 닫기 버튼 */}
        <button
          onClick={() => setIsDismissed(true)}
          className={`p-1.5 rounded-full transition-colors ${
            isExceeded 
              ? 'text-white/80 hover:text-white hover:bg-white/20' 
              : 'text-black/60 hover:text-black/80 hover:bg-black/10'
          }`}
          aria-label="Close"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}

